import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  EffectiveRow,
  PolicyKind,
  PolicyPhase,
  PolicyRow,
} from "../../api/companyPoliciesApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** Human names for the tiers a company can inherit from. Keyed by id rather
 *  than positional so a tier the server adds later still renders (as its raw
 *  id) instead of blank. */
const TIER_LABELS: Record<string, string> = {
  platform: "the platform",
  industry: "your industry",
  company: "your organization",
  department: "your department",
  user: "you",
};

function tierName(tier: string): string {
  return TIER_LABELS[tier] ?? tier;
}

/** How a row's action reads as a verb, for the union sentence. */
const ACTION_VERBS: Record<string, string> = {
  block: "blocks",
  redact: "requires redact",
  flag: "flags",
};

function actionOf(row: PolicyRow): string {
  return (row.definition as { action?: string })?.action ?? "";
}

function actionVerb(row: PolicyRow): string {
  const a = actionOf(row);
  return ACTION_VERBS[a] ?? a;
}

/**
 * The additive sentence for a key that more than one tier governs.
 *
 * Policies UNION: every matching tier applies at once and the most restrictive
 * wins. Two rows sharing a key is CORRECT — it is not a collision and must
 * never render as one. The LLM picker's "inherited from the default" wording
 * is wrong here: that setting OVERRIDES (one value wins) while this one adds.
 *
 * So: "platform requires redact; your organization additionally blocks."
 */
function unionSentence(rows: EffectiveRow[]): string | null {
  if (rows.length < 2) return null;
  const [first, ...rest] = rows;
  const head = `${tierName(first.tier)} ${actionVerb(first)}`;
  const tail = rest
    .map((r) => `${tierName(r.tier)} additionally ${actionVerb(r)}`)
    .join("; ");
  // Capitalised at render, not here — the caller controls the surrounding copy.
  return `${head}; ${tail}. All of these apply.`;
}

/** The two-sentence explanation of `kind`. Load-bearing, not documentation:
 *  an author who writes a judge expecting prevention has misunderstood the
 *  feature, and this is where to stop that. */
function KindGuidance() {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        <strong>Rule</strong> — deterministic patterns, can prevent output.
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        <strong>Judge</strong> — advisory; on a reply it runs after the text has
        streamed, so it flags rather than prevents.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
        If it must not be bypassable, write a rule.
      </Typography>
    </Box>
  );
}

const EMPTY_DRAFT = {
  key: "",
  phase: "both" as PolicyPhase,
  kind: "rule" as PolicyKind,
  pattern: "",
  instruction: "",
  action: "block",
};

/** The content policies this organization's turns are subject to.
 *
 *  Admin-gated for writes by the API, which enforces it independently — this
 *  panel renders the 403 rather than hiding it, because a blank panel is
 *  indistinguishable from a broken one.
 *
 *  Inherited platform and industry rows are shown READ-ONLY with their origin
 *  named. A company admin who can see a rule they cannot remove beats one
 *  wondering why their turns are being blocked by something invisible.
 */
export function PolicySettings() {
  const api = useAuthedApi();
  const [effective, setEffective] = useState<EffectiveRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  // Non-null while an existing row is loaded into the form. Only changes the
  // wording of the form; the write is the same upsert either way, since the
  // key addresses the row.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const companyIdRef = useRef<number | string | null>(null);

  // Load ONCE on mount, deliberately not keyed on `api`.
  //
  // useAuthedApi() returns a fresh object every render, so a useCallback that
  // depends on it changes identity on every state update — and an effect keyed
  // on that callback re-runs on every state update too. That is not just
  // wasteful: the reload calls setError(null), which WIPES the error a failed
  // save had just set, so a save that did not happen looks like one that did.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void (async () => {
      try {
        const me = await api.auth.me();
        companyIdRef.current = me.company_id ?? null;
        if (companyIdRef.current === null) {
          setError("No organization on this account.");
          return;
        }
        setEffective(
          await api.companyPolicies.listEffective(companyIdRef.current),
        );
        setError(null);
      } catch (e) {
        setError(message(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Re-read the union after a write. What a company is subject to is a union
   *  across tiers, so the server's recomputed company list alone would not
   *  show the inherited half. */
  const reload = useCallback(async () => {
    const id = companyIdRef.current;
    if (id === null) return;
    setEffective(await api.companyPolicies.listEffective(id));
  }, [api]);

  const write = useCallback(
    async (run: () => Promise<unknown>, what: string): Promise<boolean> => {
      setSaving(true);
      setNotice(null);
      try {
        await run();
        await reload();
        setError(null);
        setNotice(`${what} saved.`);
        return true;
      } catch (e) {
        // Leave `effective` alone so the controls snap back to what is
        // actually in effect rather than the value that failed to save.
        setError(message(e));
        // RETURNS FALSE rather than throwing: callers need to know, and this
        // swallowing the error meant a `.then()` clearing the form ran on
        // failure too — the admin's half-written regex vanished along with the
        // save that lost it.
        return false;
      } finally {
        setSaving(false);
      }
    },
    [reload],
  );

  /** Grouped by key so the union reads as one story per key. Order within a
   *  group is the server's, which is broadest-tier first. */
  const groups = useMemo(() => {
    const byKey = new Map<string, EffectiveRow[]>();
    for (const row of effective ?? []) {
      const bucket = byKey.get(row.key);
      if (bucket) bucket.push(row);
      else byKey.set(row.key, [row]);
    }
    return [...byKey.entries()];
  }, [effective]);

  /** Every key this company is already subject to, at any tier. Feeds the key
   *  combobox so tightening an inherited rule is a pick rather than a retype —
   *  a near-miss spelling creates an unrelated policy that governs nothing. */
  const knownKeys = useMemo(
    () => [...new Set((effective ?? []).map((r) => r.key))].sort(),
    [effective],
  );

  const submitDraft = () => {
    const definition =
      draft.kind === "rule"
        ? {
            patterns: [draft.pattern],
            action:
              draft.action === "redact"
                ? ("redact" as const)
                : ("block" as const),
          }
        : {
            instruction: draft.instruction,
            action:
              draft.action === "flag" ? ("flag" as const) : ("block" as const),
          };
    void write(
      () =>
        api.companyPolicies.save({
          key: draft.key.trim(),
          phase: draft.phase,
          kind: draft.kind,
          definition,
          enabled: true,
        }),
      `Policy ${draft.key.trim()}`,
    ).then((ok) => {
      // Only on success. A failed save leaves the draft exactly as typed so the
      // author can fix the error rather than retype the policy.
      if (ok) {
        setDraft(EMPTY_DRAFT);
        setEditingKey(null);
      }
    });
  };

  return (
    <>
      <PageHeader title="Content policies" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}

      <Stack spacing={2}>
        <WorkspaceCard interactive={false}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                In force for this organization
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                Every policy below applies to your turns. Rules from a broader
                tier are shown read-only — you can see them, but only the tier
                that set them can change them. More than one tier can govern the
                same key; when they do, all of them apply and the most
                restrictive outcome wins.
              </Typography>
            </Box>

            {groups.length === 0 && effective !== null && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No policies apply to this organization yet.
              </Typography>
            )}

            {groups.map(([key, rows]) => {
              const sentence = unionSentence(rows);
              return (
                <Box key={key}>
                  <Stack spacing={1}>
                    {rows.map((row) => (
                      <Stack
                        key={`${row.tier}-${row.scope_id}-${row.phase}-${row.kind}`}
                        direction="row"
                        spacing={1.5}
                        sx={{ alignItems: "center", flexWrap: "wrap" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, minWidth: 140 }}
                        >
                          {row.key}
                        </Typography>
                        <Chip size="small" label={row.kind} />
                        <Chip
                          size="small"
                          label={row.phase}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={actionOf(row)}
                          variant="outlined"
                        />

                        {row.inherited ? (
                          <>
                            {/* Origin, named. Without it an admin cannot tell
                                where an unremovable rule came from. */}
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Set by {tierName(row.tier)}. Read-only here.
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Switch
                              size="small"
                              checked={row.enabled}
                              disabled={saving}
                              slotProps={{
                                input: { "aria-label": `${row.key} enabled` },
                              }}
                              onChange={(e) =>
                                void write(
                                  () =>
                                    api.companyPolicies.save({
                                      key: row.key,
                                      phase: row.phase,
                                      kind: row.kind,
                                      enabled: e.target.checked,
                                    }),
                                  `Policy ${row.key}`,
                                )
                              }
                            />
                            <Button
                              size="small"
                              disabled={saving}
                              aria-label={`Edit ${row.key}`}
                              onClick={() => {
                                // Load the row into the authoring form. The key
                                // is what addresses the row, so saving under it
                                // is an update — no delete/recreate, and no
                                // window where the scope silently re-inherits.
                                const d = (row.definition ??
                                  {}) as unknown as Record<string, unknown>;
                                setDraft({
                                  key: row.key,
                                  phase: row.phase,
                                  kind: row.kind,
                                  pattern: Array.isArray(d.patterns)
                                    ? (d.patterns as string[]).join("\n")
                                    : "",
                                  instruction:
                                    typeof d.instruction === "string"
                                      ? d.instruction
                                      : "",
                                  action:
                                    typeof d.action === "string"
                                      ? d.action
                                      : "block",
                                });
                                setEditingKey(row.key);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              disabled={saving}
                              aria-label={`Remove ${row.key}`}
                              onClick={() =>
                                void write(
                                  () =>
                                    api.companyPolicies.remove(
                                      row.key,
                                      row.phase,
                                      row.kind,
                                    ),
                                  `Policy ${row.key}`,
                                )
                              }
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </Stack>
                    ))}

                    {/* The union, stated additively. NOT "overridden": a
                        company row does not replace a platform one — both
                        apply, and saying otherwise teaches the opposite of
                        how the feature works. */}
                    {sentence && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {sentence}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </WorkspaceCard>

        <WorkspaceCard interactive={false}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Add a policy for this organization
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                Adding a policy for a key a broader tier already governs does
                not replace it — yours applies on top.
              </Typography>
            </Box>

            <KindGuidance />

            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              {/*
                A combobox, not a plain input, and the spec calls this
                load-bearing. The engine treats `key` as opaque ON PURPOSE —
                platform `pii.ssn` and your `pii.ssn` both applying IS the union
                working. But an admin who types `pii_ssn` believing they are
                tightening the platform rule silently creates an unrelated
                policy that governs nothing at all. Offering the keys this
                company is actually subject to makes tightening a pick rather
                than a retype. `freeSolo` keeps a genuinely new key possible.
              */}
              <Autocomplete
                freeSolo
                size="small"
                options={knownKeys}
                sx={{ maxWidth: 220, flexGrow: 1 }}
                disabled={saving}
                inputValue={draft.key}
                onInputChange={(_e, value) =>
                  setDraft({ ...draft, key: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Policy key"
                    helperText="Reuse an existing key to tighten it."
                  />
                )}
              />

              <FormControl
                size="small"
                sx={{ minWidth: 140 }}
                disabled={saving}
              >
                <InputLabel id="policy-kind-label">Kind</InputLabel>
                <Select
                  labelId="policy-kind-label"
                  label="Kind"
                  value={draft.kind}
                  SelectDisplayProps={{ "aria-label": "Policy kind" }}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      kind: e.target.value as PolicyKind,
                      // A judge cannot redact; reset to a valid action.
                      action: e.target.value === "judge" ? "flag" : "block",
                    })
                  }
                >
                  <MenuItem value="rule">Rule</MenuItem>
                  <MenuItem value="judge">Judge</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: 140 }}
                disabled={saving}
              >
                <InputLabel id="policy-phase-label">Runs on</InputLabel>
                <Select
                  labelId="policy-phase-label"
                  label="Runs on"
                  value={draft.phase}
                  SelectDisplayProps={{ "aria-label": "Policy phase" }}
                  onChange={(e) =>
                    setDraft({ ...draft, phase: e.target.value as PolicyPhase })
                  }
                >
                  <MenuItem value="pre">The prompt</MenuItem>
                  <MenuItem value="post">The reply</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: 140 }}
                disabled={saving}
              >
                <InputLabel id="policy-action-label">Action</InputLabel>
                <Select
                  labelId="policy-action-label"
                  label="Action"
                  value={draft.action}
                  SelectDisplayProps={{ "aria-label": "Policy action" }}
                  onChange={(e) =>
                    setDraft({ ...draft, action: String(e.target.value) })
                  }
                >
                  <MenuItem value="block">Block</MenuItem>
                  {draft.kind === "rule" ? (
                    <MenuItem value="redact">Redact</MenuItem>
                  ) : (
                    <MenuItem value="flag">Flag</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>

            {draft.kind === "rule" ? (
              <TextField
                size="small"
                label="Pattern"
                disabled={saving}
                value={draft.pattern}
                helperText="A regular expression matched against the text."
                onChange={(e) =>
                  setDraft({ ...draft, pattern: e.target.value })
                }
                slotProps={{ htmlInput: { "aria-label": "Rule pattern" } }}
              />
            ) : (
              <TextField
                size="small"
                label="Instruction"
                multiline
                minRows={2}
                disabled={saving}
                value={draft.instruction}
                helperText="What the judge should look for. It advises; it cannot un-send a reply."
                onChange={(e) =>
                  setDraft({ ...draft, instruction: e.target.value })
                }
                slotProps={{ htmlInput: { "aria-label": "Judge instruction" } }}
              />
            )}

            <Box>
              <Button
                variant="contained"
                size="small"
                disabled={
                  saving ||
                  !draft.key.trim() ||
                  (draft.kind === "rule"
                    ? !draft.pattern.trim()
                    : !draft.instruction.trim())
                }
                onClick={submitDraft}
              >
                {editingKey ? "Save changes" : "Add policy"}
              </Button>
            </Box>
          </Stack>
        </WorkspaceCard>
      </Stack>
    </>
  );
}
